// Utility functions for managing favicons across the application

export const generatePastelColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = hash % 360;
    return `hsla(${h}, 70%, 80%, 0.2)`;
};

export const createUserFavicon = (initials: string, backgroundColor: string) => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
        // Convert HSLA to RGB, then blend with white background to get the actual visual color
        const hslaMatch = backgroundColor.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%,\s*([\d.]+)\)/);
        
        let solidBg = backgroundColor;
        if (hslaMatch) {
            const hue = parseInt(hslaMatch[1]);
            const saturation = parseInt(hslaMatch[2]) / 100;
            const lightness = parseInt(hslaMatch[3]) / 100;
            const alpha = parseFloat(hslaMatch[4]);
            
            // Convert HSL to RGB
            const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
            const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
            const m = lightness - c / 2;
            
            let r, g, b;
            if (hue >= 0 && hue < 60) {
                [r, g, b] = [c, x, 0];
            } else if (hue >= 60 && hue < 120) {
                [r, g, b] = [x, c, 0];
            } else if (hue >= 120 && hue < 180) {
                [r, g, b] = [0, c, x];
            } else if (hue >= 180 && hue < 240) {
                [r, g, b] = [0, x, c];
            } else if (hue >= 240 && hue < 300) {
                [r, g, b] = [x, 0, c];
            } else {
                [r, g, b] = [c, 0, x];
            }
            
            // Add m and convert to 0-255 range
            r = Math.round((r + m) * 255);
            g = Math.round((g + m) * 255);
            b = Math.round((b + m) * 255);
            
            // Alpha blend with white background (255, 255, 255)
            const finalR = Math.round(r * alpha + 255 * (1 - alpha));
            const finalG = Math.round(g * alpha + 255 * (1 - alpha));
            const finalB = Math.round(b * alpha + 255 * (1 - alpha));
            
            solidBg = `rgb(${finalR}, ${finalG}, ${finalB})`;
        } else {
            // Fallback: just make it opaque
            solidBg = backgroundColor.replace('0.2)', '1)');
        }
        
        // Create circular background
        ctx.beginPath();
        ctx.arc(32, 32, 32, 0, Math.PI * 2);
        ctx.fillStyle = solidBg;
        ctx.fill();
        
        // Add subtle border for definition
        ctx.beginPath();
        ctx.arc(32, 32, 31, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Draw text with better contrast
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials, 32, 32);
        
        // Update all favicon links to ensure it works across different browsers
        const links = document.querySelectorAll("link[rel*='icon']") as NodeListOf<HTMLLinkElement>;
        const dataUrl = canvas.toDataURL('image/png');
        
        links.forEach(link => {
            link.href = dataUrl;
        });
        
        // If no favicon link exists, create one
        if (links.length === 0) {
            const newLink = document.createElement('link');
            newLink.rel = 'shortcut icon';
            newLink.type = 'image/png';
            newLink.href = dataUrl;
            document.getElementsByTagName('head')[0].appendChild(newLink);
        }
    }
};

export const resetToDefaultFavicon = () => {
    const links = document.querySelectorAll("link[rel*='icon']") as NodeListOf<HTMLLinkElement>;
    links.forEach(link => {
        link.href = '/favicon.svg';
    });
    
    // Also reset the title to default
    document.title = 'QuickCruit - Your Dream Job Awaits';
};

export const setPortfolioFavicon = (firstName: string, lastName: string) => {
    if (firstName && lastName) {
        const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        const backgroundColor = generatePastelColor(firstName + lastName);
        createUserFavicon(initials, backgroundColor);
        document.title = `${firstName} ${lastName}`;
    }
}; 